/**
 * Progress — §7.10 (mission 6 WP03 T020; FR-005; style guide §4.5).
 *
 * A 6px navy-200 track with a Sky-burst fill. `value: null` is the
 * indeterminate variant: a 30% segment moving left to right over 1.2s.
 *
 * This is the one component allowed `bg-[var(--color-accent)]` (post-plan T6;
 * the style-guide job allowlists this file and the run stepper). The fill
 * carries `data-progress-fill`, which globals.css exempts from the reduced
 * motion rule: progress still animates its width (§4.5, post-tasks P21). The
 * caller names the bar (`aria-label` or `aria-labelledby`) and renders the
 * label pair above it.
 */
import * as React from 'react'

import { cn } from './cn'

export interface ProgressProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** The current count, or null while the total is unknown (indeterminate). */
  value: number | null
  max?: number
}

export function Progress({ value, max = 100, className, ...props }: ProgressProps) {
  const determinate = value !== null
  const percent = determinate && max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={determinate ? value : undefined}
      className={cn('relative h-1.5 w-full overflow-hidden rounded-full bg-navy-200', className)}
      {...props}
    >
      {/* mission 6 WP13: data-progress-indeterminate lets globals.css keep the sweep under reduced motion (N3, §4.5). */}
      <div
        data-progress-fill=""
        data-progress-indeterminate={determinate ? undefined : ''}
        className={
          determinate
            ? 'h-full rounded-full bg-[var(--color-accent)] transition-[width] duration-[var(--duration-base)] ease-[var(--ease-out)]'
            : 'h-full w-[30%] rounded-full bg-[var(--color-accent)] animate-[trellis-indeterminate_1.2s_ease-in-out_infinite]'
        }
        style={determinate ? { width: `${percent}%` } : undefined}
      />
    </div>
  )
}
